import { ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarGrid } from './CalendarGrid';
import { CalendarPopper } from './CalendarPopper';
import {
  CalendarToggle,
  FieldMessage,
  FieldRoot,
  FieldShell,
  SegmentInput,
  describeProblem,
  type FieldSize,
  type FieldVariant,
  type IconPlacement,
  type TypedProblem,
} from './FieldParts';
import { formatIsoDate, normalizeIso, type IsoDate } from './isoDate';
import { followTypedDay, monthLimits, openingPanels, type MonthIndex } from './panels';
import { useCalendarPopover } from './useCalendarPopover';

export interface SingleDateFieldProps {
  /** `'YYYY-MM-DD'`, or `null` for empty. */
  value: IsoDate | null;
  onChange: (value: IsoDate | null) => void;
  minDate?: IsoDate | null;
  maxDate?: IsoDate | null;
  /**
   * The month the feature proposes when the field is empty (spec *Default panel positions*
   * 2) — the correction journal's financial-year end. Default: the current month.
   */
  referenceDate?: IsoDate | null;
  /**
   * `false` makes the field a button that only opens the calendar — for a dense row where
   * the date must be picked, never typed. Default `true`: type `DD.MM.YYYY` or pick.
   */
  typing?: boolean;
  size?: FieldSize;
  /** `outlined` (default) or `text` — borderless at rest, for a value in a properties list. */
  variant?: FieldVariant;
  /**
   * The calendar glyph. Default `true`; a dense inline field may drop it, since a press on
   * the field and ArrowDown open the calendar too.
   */
  icon?: boolean;
  /** Mount focused with the calendar open. */
  autoOpen?: boolean;
  /** Emptying the typed text clears the date (default). `false` for a required date: the text is put back. */
  clearable?: boolean;
  /**
   * Publish a pick or a typed date equal to `value` too, for a field whose commit is itself
   * the act (a correction row awaiting activation). Default `false`: an unchanged value is dropped.
   */
  commitUnchanged?: boolean;
  /** Hears the calendar open and close. */
  onOpenChange?: (open: boolean) => void;
  /** Trailing (default) or leading glyph — the period editor draws it first. */
  iconPlacement?: IconPlacement;
  /**
   * Text after the date inside the field — `– open` for a period with no end, which is
   * one picked day plus a word, not a second date to edit.
   */
  suffix?: ReactNode;
  placeholder?: string;
  /** Accessible name; default "Date". */
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  width?: number | string;
  'data-testid'?: string;
}

/** After the date, in the placeholder's quieter ink: it describes, it is not a value. */
const Suffix = styled('span')(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  whiteSpace: 'nowrap',
  color: theme.palette.text.tertiary,
}));

const ButtonValue = styled('span', {
  shouldForwardProp: (prop) => prop !== 'empty',
})<{ empty: boolean }>(({ theme, empty }) => ({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  ...(empty && { color: theme.palette.text.placeholder }),
}));

/**
 * ONE DATE — typed or picked from a one-month calendar (the platform calendar spec's single
 * mode; Figma `Variant3` `40245:261809`).
 *
 * A pick publishes and closes; a typed date publishes on blur or Enter (which closes the
 * calendar too). Escape puts the
 * text back and closes without changes. ArrowDown from the input (or Enter/Space/ArrowDown
 * on the button form) moves focus into the grid, where the arrow keys, PageUp/PageDown
 * (with Shift: a year) and Home/End walk the days. A refused entry says why under the field.
 */
export function SingleDateField({
  value: rawValue,
  onChange,
  minDate,
  maxDate,
  referenceDate,
  typing = true,
  size = 'medium',
  variant = 'outlined',
  icon = true,
  autoOpen = false,
  clearable = true,
  commitUnchanged = false,
  onOpenChange,
  iconPlacement = 'end',
  suffix,
  placeholder,
  label,
  disabled,
  readOnly,
  error,
  fullWidth,
  width,
  'data-testid': testId = 'single-date-field',
}: SingleDateFieldProps) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  const value = normalizeIso(rawValue);
  const popover = useCalendarPopover({ onOpenChange });
  const { openCalendar } = popover;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const limits = useMemo(() => monthLimits(minDate, maxDate), [minDate, maxDate]);
  const [months, setMonths] = useState<MonthIndex[]>(() =>
    openingPanels({ count: 1, start: value, referenceDate, limits }),
  );
  const [problem, setProblemState] = useState<TypedProblem | null>(null);
  const onProblem = useCallback((next: TypedProblem | null) => setProblemState(next), []);
  /** Bumped when Escape puts the value back, so the text follows. */
  const [revision, setRevision] = useState(0);
  const messageId = `${useId().replace(/:/g, '')}-message`;
  const accessibleName = label ?? String(t('calendar_date'));
  const inert = disabled || readOnly;
  const message = describeProblem(t, problem);
  const visibleMessage = size === 'medium';

  const show = (focusGrid = false) => {
    if (inert) return;
    if (popover.open) {
      if (focusGrid) popover.focusGrid();
      return;
    }
    setMonths(openingPanels({ count: 1, start: value, referenceDate, limits }));
    popover.openCalendar(focusGrid ? 0 : null);
  };
  const returnTarget = () => (typing ? inputRef.current : buttonRef.current);
  /** The glyph opens with focus IN the field, so typing and Escape work straight away. */
  const toggle = () => {
    if (popover.open) return popover.closeCalendar();
    returnTarget()?.focus();
    show();
  };

  const autoOpened = useRef(false);
  useEffect(() => {
    if (!autoOpen || autoOpened.current) return;
    autoOpened.current = true;
    (typing ? inputRef.current : buttonRef.current)?.focus();
    openCalendar();
  }, [autoOpen, openCalendar, typing]);

  const toggleButton = icon && !readOnly && (
    <CalendarToggle
      open={popover.open}
      onToggle={toggle}
      disabled={inert}
      size={size}
      controls={popover.popperId}
      testId={`${testId}-toggle`}
    />
  );

  const onFieldKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown' || (!typing && (event.key === 'Enter' || event.key === ' '))) {
      event.preventDefault();
      event.stopPropagation();
      show(true);
    } else if (event.key === 'Escape' && popover.open) {
      // Close without changes (spec *Keyboard*); typed text was already put back.
      event.stopPropagation();
      setRevision((n) => n + 1);
      popover.closeCalendar();
    }
  };

  return (
    <>
      <FieldRoot ref={popover.fieldRef} fullWidth={fullWidth} onBlur={popover.onBlurWithin}>
        <FieldShell
          size={size}
          variant={variant}
          toggle={toggleButton}
          iconPlacement={iconPlacement}
          active={popover.open}
          error={Boolean(error || problem)}
          disabled={disabled}
          readOnly={readOnly}
          fullWidth={fullWidth}
          sx={width !== undefined ? { width } : undefined}
          onMouseDown={(event) => {
            // A press on the shell's padding lands in the input, like any text field.
            if (typing && event.target === event.currentTarget) {
              event.preventDefault();
              inputRef.current?.focus();
            }
          }}
          onClick={() => !popover.open && show()}
          data-testid={testId}
        >
          {typing ? (
            <SegmentInput
              ref={inputRef}
              value={value}
              onCommit={onChange}
              commitUnchanged={commitUnchanged}
              allowEmpty={clearable}
              revision={revision}
              onTypedDay={(day) =>
                setMonths((panels) => followTypedDay(panels, 'start', day, limits))
              }
              onProblemChange={onProblem}
              // An accepted date entered with Enter is the last act, as Enter in a range's end.
              onEnter={() => popover.open && popover.closeCalendar()}
              aria-describedby={problem ? messageId : undefined}
              title={!visibleMessage && message ? message : undefined}
              minDate={minDate}
              maxDate={maxDate}
              size={size}
              // With a suffix the date keeps its own width, so the word sits right after it.
              grow={!suffix}
              placeholder={placeholder}
              aria-label={accessibleName}
              aria-expanded={popover.open}
              aria-controls={popover.open ? popover.popperId : undefined}
              disabled={disabled}
              readOnly={readOnly}
              onKeyDown={onFieldKeyDown}
              data-testid={`${testId}-input`}
            />
          ) : (
            <ButtonBase
              ref={buttonRef}
              disableRipple
              disabled={inert}
              aria-label={`${accessibleName}${value ? `: ${formatIsoDate(value)}` : ''}`}
              aria-haspopup='dialog'
              aria-expanded={popover.open}
              aria-controls={popover.open ? popover.popperId : undefined}
              onKeyDown={onFieldKeyDown}
              onClick={(event) => {
                event.stopPropagation();
                toggle();
              }}
              sx={{ flex: 1, minWidth: 0, height: '100%', font: 'inherit', color: 'inherit' }}
              data-testid={`${testId}-button`}
            >
              <ButtonValue empty={!value}>
                {value ? formatIsoDate(value) : (placeholder ?? t('calendar_date_placeholder'))}
              </ButtonValue>
            </ButtonBase>
          )}
          {suffix && <Suffix data-testid={`${testId}-suffix`}>{suffix}</Suffix>}
        </FieldShell>
        <FieldMessage id={messageId} message={message} visible={visibleMessage} />
      </FieldRoot>
      <CalendarPopper
        ref={popover.paperRef}
        id={popover.popperId}
        open={popover.open}
        anchorEl={popover.fieldRef.current}
        fieldEl={popover.fieldRef.current}
        onClose={(reason) => {
          if (reason === 'escape') setRevision((n) => n + 1);
          popover.closeCalendar(reason === 'escape' ? returnTarget() : null);
        }}
        onBlur={popover.onBlurWithin}
        label={accessibleName}
        layoutKey={visibleMessage && message ? 'message' : 'plain'}
        data-testid={`${testId}-calendar`}
      >
        <CalendarGrid
          mode='single'
          value={value}
          onPick={(day) => {
            setRevision((n) => n + 1);
            popover.closeCalendar(returnTarget());
            if (commitUnchanged || day !== value) onChange(day);
          }}
          months={months}
          onMonthsChange={setMonths}
          minDate={minDate}
          maxDate={maxDate}
          autoFocusPanel={popover.focusPanelOnOpen}
        />
      </CalendarPopper>
    </>
  );
}
